import { InsuranceType } from '@weave/schema-gen-ts/dist/schemas/insurance-verification/shared/enums.pb';
import { InsuranceQueries } from '@frontend/api-insurance';
import { getWeaveToken } from '@frontend/auth-helpers';
import { useInsuranceType } from '../providers/insurance-type-provider';
import { usePersonInfoStore } from '../providers/person-info-provider';
import { base64ToPdfBlob } from '../utils/utils';
import { useGetLocationVerticalForPayerList } from './use-get-location-vertical-for-payer-list';

interface GetInsuranceEligibilityReportUrlParams {
  locationId: string;
  personId: string;
  sourceId: string;
  personPMSId: string;
  showTuuthfairyReport?: boolean;
  insuranceType?: InsuranceType;
}

const getInsuranceEligibilityReportUrl = ({
  locationId,
  personId,
  sourceId,
  personPMSId,
  showTuuthfairyReport = false,
  insuranceType = InsuranceType.INSURANCE_TYPE_PRIMARY,
}: GetInsuranceEligibilityReportUrlParams) => {
  const baseUrl = window.location.origin;
  const userToken = getWeaveToken();
  // TODO - SECURITY PROBLEM - NEVER EMBED USER AUTH TOKENS IN URLS
  return `${baseUrl}/insurance-eligibility-report/?locationId=${locationId}&personId=${personId}&sourceId=${sourceId}&personPMSId=${personPMSId}&showTuuthfairyReport=${showTuuthfairyReport}&authToken=${userToken}&insuranceType=${insuranceType}`;
};

type UseOpenInsuranceReportParams = {
  requestId?: string;
  shouldShowTuuthFairyReport?: boolean;
  personLocationId?: string;
  personID?: string;
  personSourceId?: string;
  personPMSID?: string;
};

export const useOpenInsuranceReport = ({
  requestId = '',
  shouldShowTuuthFairyReport,
  personLocationId = '',
  personID = '',
  personSourceId = '',
  personPMSID = '',
}: UseOpenInsuranceReportParams) => {
  const { personInfo, locationId: locationID } = usePersonInfoStore();
  const personId = (personInfo?.PersonID as string) || personID;
  const sourceId = personInfo?.SourceID || personSourceId;
  const personPMSId = personInfo?.PMID || personPMSID;
  const locationId = locationID || personLocationId || '';

  const { isDental, isWellnessVertical } = useGetLocationVerticalForPayerList(locationId || personLocationId || '');
  const { insuranceType } = useInsuranceType();
  const {
    isLoading,
    isRefetching,
    refetch: getInsuranceEligibilityReportForNonDental,
  } = InsuranceQueries.useGetNonDentalInsuranceEligibilityReport(locationId || personLocationId || '', requestId, {
    enabled: false,
  });

  const handleOpenInsuranceReportForNonDental = (insuranceEligibilityReportForNonDental: string) => {
    if (!isLoading && insuranceEligibilityReportForNonDental) {
      const pdfBlob = base64ToPdfBlob(insuranceEligibilityReportForNonDental);
      const url = URL.createObjectURL(pdfBlob);

      // Open the URL in a new window
      window.open(url, 'noopener,noreferrer');

      // Clean up the URL object to prevent memory leaks
      URL.revokeObjectURL(url);
    }
  };

  const handleOpenInsuranceReportForDental = () => {
    const url = getInsuranceEligibilityReportUrl({
      personId,
      locationId: locationId ?? '',
      sourceId: sourceId ?? '',
      personPMSId: personPMSId ?? '',
      showTuuthfairyReport: shouldShowTuuthFairyReport,
      insuranceType: insuranceType,
    });
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const handleOpenInsuranceReport = () => {
    if (isDental) {
      handleOpenInsuranceReportForDental();
    } else if (isWellnessVertical) {
      getInsuranceEligibilityReportForNonDental().then((eligibilityReport) => {
        if (eligibilityReport?.data?.file) {
          handleOpenInsuranceReportForNonDental(eligibilityReport?.data?.file);
        }
      });
    }
  };

  return { handleOpenInsuranceReport, isLoading: isLoading || isRefetching };
};
